/**
 * MD2Pro Analytics - Lightweight frontend tracking
 *
 * Usage:
 *   import { initAnalytics, trackEvent } from './md2pro-analytics';
 *   initAnalytics('my-app-name');
 *   trackEvent('button_click', { buttonId: 'signup' });
 */

const ANALYTICS_ENDPOINT = 'https://waty8tf0sc.execute-api.us-east-1.amazonaws.com/collect';
const BATCH_INTERVAL = 5000; // Send batched events every 5 seconds
let eventQueue = [];
let appName = 'unknown';
let sessionId = '';
let flushTimer = null;

function getSessionId() {
  const key = 'md2pro_sid';
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

function getVisitorId() {
  const key = 'md2pro_vid';
  let vid = localStorage.getItem(key);
  if (!vid) {
    vid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, vid);
  }
  return vid;
}

function baseEvent(eventName, data = {}) {
  return {
    event: eventName,
    app: appName,
    sessionId,
    visitorId: getVisitorId(),
    page: window.location.pathname,
    referrer: document.referrer || '',
    title: document.title || '',
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    timestamp: new Date().toISOString(),
    data,
  };
}

async function flush() {
  if (eventQueue.length === 0) return;

  const batch = [...eventQueue];
  eventQueue = [];

  try {
    const resp = await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch),
      keepalive: true,
    });
    if (!resp.ok) {
      // Put events back if send failed
      eventQueue.unshift(...batch);
    }
  } catch {
    // Put events back on network error
    eventQueue.unshift(...batch);
  }
}

function startBatching() {
  if (flushTimer) return;
  flushTimer = setInterval(flush, BATCH_INTERVAL);

  // Flush on page unload
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    window.addEventListener('beforeunload', flush);
  }
}

/**
 * Initialize analytics for this app
 * @param {string} name - App identifier (e.g., 'jvd-realty', 'milili-salons')
 */
export function initAnalytics(name) {
  appName = name;
  sessionId = getSessionId();
  startBatching();

  // Track initial pageview
  trackEvent('pageview');

  // Track route changes (for SPAs)
  if (typeof window !== 'undefined') {
    // Listen for popstate (back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(() => trackEvent('pageview'), 0);
    });

    // Patch pushState and replaceState for SPA navigation
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;

    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      setTimeout(() => trackEvent('pageview'), 0);
    };

    window.history.replaceState = function (...args) {
      origReplace.apply(this, args);
      setTimeout(() => trackEvent('pageview'), 0);
    };
  }

  // Track web vitals if available
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      // Largest Contentful Paint
      new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) trackEvent('web_vital', { metric: 'LCP', value: Math.round(last.startTime) });
      }).observe({ type: 'largest-contentful-paint', buffered: true });

      // First Input Delay
      new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries[0]) trackEvent('web_vital', { metric: 'FID', value: Math.round(entries[0].processingStart - entries[0].startTime) });
      }).observe({ type: 'first-input', buffered: true });

      // Cumulative Layout Shift
      let clsValue = 0;
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) clsValue += entry.value;
        }
        trackEvent('web_vital', { metric: 'CLS', value: Math.round(clsValue * 1000) / 1000 });
      }).observe({ type: 'layout-shift', buffered: true });
    } catch {
      // PerformanceObserver not fully supported — skip
    }
  }

  // Track JS errors
  window.addEventListener('error', (e) => {
    trackEvent('js_error', {
      message: e.message,
      source: e.filename,
      line: e.lineno,
      col: e.colno,
    });
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    trackEvent('js_error', {
      message: e.reason?.message || String(e.reason),
      type: 'unhandled_promise',
    });
  });
}

/**
 * Track a custom event
 * @param {string} eventName - Event name (e.g., 'button_click', 'form_submit')
 * @param {Object} data - Additional event data
 */
export function trackEvent(eventName, data = {}) {
  eventQueue.push(baseEvent(eventName, data));
}
